export type CategoryBucketKey = 'fiction' | 'nonFiction' | 'childrenYa' | 'poetryShort' | 'coverAudio';

export interface CategoryLike {
  display_name?: string | null;
  name?: string | null;
  path?: string | null;
  category_path?: string | null;
  tier_1?: string | null;
  category_tier_1?: string | null;
  tier_2?: string | null;
  category_tier_2?: string | null;
  tier_3?: string | null;
  category_tier_3?: string | null;
  bisac_codes?: unknown;
  bisac?: unknown;
}

export interface CategoryBucketSummary {
  fiction: number;
  nonFiction: number;
  childrenYa: number;
  poetryShort: number;
  coverAudio: number;
}

export const EMPTY_CATEGORY_SUMMARY: CategoryBucketSummary = {
  fiction: 0,
  nonFiction: 0,
  childrenYa: 0,
  poetryShort: 0,
  coverAudio: 0,
};

function normalizeBisacCodes(input: unknown): string[] {
  if (!input) return [];
  if (Array.isArray(input)) return input.filter((code): code is string => typeof code === 'string');
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) {
        return parsed.filter((code): code is string => typeof code === 'string');
      }
    } catch {
      // fall through to comma separation if not JSON
      return input
        .split(',')
        .map(code => code.trim())
        .filter(code => code.length > 0);
    }
  }
  return [];
}

function textIncludes(source: unknown, fragment: string): boolean {
  if (!source) return false;
  return String(source).toLowerCase().includes(fragment);
}

// Shared heuristic for assigning a category to a content bucket.
export function bucketCategory<T extends CategoryLike>(cat: T | null | undefined): CategoryBucketKey {
  const name = String(cat?.display_name || cat?.name || '').toLowerCase();
  const path = String(cat?.path || cat?.category_path || '').toLowerCase();
  const tier1 = String(cat?.tier_1 || cat?.category_tier_1 || '').toLowerCase();
  const tier2 = String(cat?.tier_2 || cat?.category_tier_2 || '').toLowerCase();
  const bisacCodes = normalizeBisacCodes(cat?.bisac_codes || cat?.bisac);

  const hasPrefix = (prefix: string) => bisacCodes.some(code => code.toUpperCase().startsWith(prefix));

  const isChildrenYA = (
    hasPrefix('JUV') || hasPrefix('JNF') || hasPrefix('YAF') || hasPrefix('YAN') ||
    textIncludes(tier1, 'children') || textIncludes(tier1, 'young adult') || tier1.includes('ya') ||
    textIncludes(tier2, 'young adult') || textIncludes(tier2, 'children') || textIncludes(tier2, 'picture') || textIncludes(tier2, 'middle grade') ||
    textIncludes(name, 'middle grade') || textIncludes(name, 'picture book') || textIncludes(name, 'board book') ||
    textIncludes(name, 'kid') || textIncludes(name, 'child')
  );
  if (isChildrenYA) return 'childrenYa';

  const isCoverAudio = (
    textIncludes(name, 'cover') || textIncludes(name, 'design') || textIncludes(name, 'jacket') || textIncludes(name, 'illustration') ||
    textIncludes(name, 'audio') || textIncludes(name, 'audiobook') || textIncludes(name, 'narration') ||
    textIncludes(path, 'cover') || textIncludes(path, 'design') || textIncludes(path, 'audio')
  );
  if (isCoverAudio) return 'coverAudio';

  const isPoetryOrShort = hasPrefix('POE') || textIncludes(tier1, 'poetry') || textIncludes(name, 'poetry') || textIncludes(name, 'haiku') || textIncludes(name, 'verse') || textIncludes(name, 'short stor');
  if (isPoetryOrShort) return 'poetryShort';

  const isNonFictionWord =
    /\bnon[- ]?fiction\b/.test(name) ||
    /\bnon[- ]?fiction\b/.test(tier1) ||
    /\bnon[- ]?fiction\b/.test(tier2) ||
    textIncludes(name, 'memoir') ||
    textIncludes(name, 'biograph') ||
    textIncludes(name, 'essay') ||
    textIncludes(name, 'journalism');
  if (isNonFictionWord) return 'nonFiction';

  const isFictionWord = textIncludes(tier1, 'fiction') || textIncludes(tier2, 'fiction') || textIncludes(name, 'fiction');
  if (hasPrefix('FIC') || isFictionWord) return 'fiction';

  return 'nonFiction';
}

export function groupCategoriesByBucket<T extends CategoryLike>(
  categories: T[],
  options?: { sort?: boolean }
): Record<CategoryBucketKey, T[]> {
  const groups: Record<CategoryBucketKey, T[]> = {
    fiction: [],
    nonFiction: [],
    childrenYa: [],
    poetryShort: [],
    coverAudio: [],
  };

  categories.forEach((cat) => {
    const bucket = bucketCategory(cat);
    groups[bucket].push(cat);
  });

  if (options?.sort) {
    const sorter = (a: T, b: T) => {
      const an = String(a?.display_name || a?.name || '').toLowerCase();
      const bn = String(b?.display_name || b?.name || '').toLowerCase();
      return an.localeCompare(bn);
    };
    (Object.keys(groups) as CategoryBucketKey[]).forEach((key) => {
      groups[key] = [...groups[key]].sort(sorter);
    });
  }

  return groups;
}

export function summarizeCategoryBuckets<T extends CategoryLike>(categories: T[]): CategoryBucketSummary {
  const groups = groupCategoriesByBucket(categories);
  return {
    fiction: groups.fiction.length,
    nonFiction: groups.nonFiction.length,
    childrenYa: groups.childrenYa.length,
    poetryShort: groups.poetryShort.length,
    coverAudio: groups.coverAudio.length,
  };
}
